import React, { useState } from 'react';
// import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Paper, Stack, Button, Divider } from '@mui/material';

// Provider images
import maheshImg from '../assets/provider_headshots/mahesh_kumar.png';
import betsyImg from '../assets/provider_headshots/betsy_john_kuruvilla.jpg';
import raghuImg from '../assets/provider_headshots/raghu_gandhi.jpg';
import michelleImg from '../assets/provider_headshots/michelle_garcia.png';
import lilianaImg from '../assets/provider_headshots/liliana_sivak.jpeg';

const ScheduleAndProviders = () => {
  const providers = [
    {
      id: 'mahesh',
      label: 'Mahesh Kumar, MD',
      img: maheshImg,
      url: 'https://www.patientfusion.com/external/appointment/b8053512-aeb2-4fe1-b127-b9fa4327f78b?origin=doctor',
    },
    {
      id: 'betsy',
      label: 'Betsy Kuruvilla, MSN, PMHNP-BC',
      img: betsyImg,
      url: 'https://www.patientfusion.com/external/appointment/4b414e6b-5f20-42b0-875a-0a6a844e6688?origin=doctor',
    },
    {
      id: 'raghu',
      label: 'Raghu Gandhi, MD',
      img: raghuImg,
      url: '',
    },
    {
      id: 'michelle',
      label: 'Michelle Garcia, LCSW',
      img: michelleImg,
      url: '',
    },
    {
      id: 'liliana',
      label: 'Liliana Sivak, MS, LPC',
      img: lilianaImg,
      url: '',
    },
  ];

  const defaultProvider = providers.find((p) => p.url)?.id || providers[0].id;

  const [selectedProvider, setSelectedProvider] = useState(defaultProvider);

  const activeProvider = providers.find((p) => p.id === selectedProvider);

  return (
    <>
      <Helmet>
        <title>Schedule an Appointment | Aspire Behavioral Health</title>
        <meta
          name="description"
          content="Schedule an appointment with Aspire Behavioral Health. Select a provider, complete patient forms, and request mental health care online."
        />
      </Helmet>

      <Box
        sx={{
          backgroundColor: 'white',
          minHeight: '100vh',
          py: 8,
          px: { xs: 2, md: 6 },
        }}
      >
        <Paper
          elevation={4}
          sx={{
            maxWidth: 950,
            mx: 'auto',
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            backgroundColor: '#fff',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: '#3a4c17',
              fontWeight: 700,
              mb: 2,
              textAlign: 'center',
            }}
          >
            Schedule an Appointment
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: '750px',
              mx: 'auto',
              mb: 3,
              textAlign: 'center',
              color: 'text.secondary',
              lineHeight: 1.8,
            }}
          >
            Select a provider below to schedule your appointment. You can also
            call us at <strong>(210) 866-9850</strong> or text{' '}
            <strong>(210) 896-4539</strong>.
          </Typography>

          {/* Top CTA */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
              mb: 4,
            }}
          >
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d3eab0',
                },
              }}
            >
              Call 210-866-9850
            </Button>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Forms */}
          <Box
            sx={{
              backgroundColor: '#e3f3c5',
              p: { xs: 3, sm: 4 },
              borderRadius: 4,
              mb: 5,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: '#3a4c17',
                fontWeight: 700,
                mb: 2,
              }}
            >
              Complete Your Forms
            </Typography>

            <Typography
              variant="body1"
              sx={{
                maxWidth: '700px',
                mx: 'auto',
                mb: 3,
                color: '#333',
                lineHeight: 1.7,
              }}
            >
              After requesting your appointment, please complete the appropriate
              form based on whether you are a new or returning patient.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                href="https://app.formdr.com/practice/NDIwNzM=/form/ZAlHhd2-m36A_AETfJ8qDMypuCh3D5l7"
                target="_blank"
                sx={{
                  backgroundColor: 'white',
                  color: '#3a4c17',
                  fontWeight: 600,
                  borderRadius: '30px',
                  px: 4,
                  py: 1.3,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                New Patient Form
              </Button>

              <Button
                variant="contained"
                href="https://app.formdr.com/practice/NDIwNzM=/form/NsjsEAivFBDF_zAsYMolF7zG7L5xDQIq"
                target="_blank"
                sx={{
                  backgroundColor: 'white',
                  color: '#3a4c17',
                  fontWeight: 600,
                  borderRadius: '30px',
                  px: 4,
                  py: 1.3,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                Follow-Up Form
              </Button>
            </Stack>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color: '#8a1f1f',
                textAlign: 'center',
                fontWeight: 500,
              }}
            >
              New patients must complete the required form before their
              appointment.
            </Typography>
          </Box>

          {/* Provider Selector */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h5"
              sx={{ color: '#3a4c17', fontWeight: 700, mb: 1 }}
            >
              Select Provider
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 3,
              }}
            >
              Choose an available provider below to view their scheduler.
            </Typography>

            <Stack direction="column" spacing={2} alignItems="center">
              {providers.map((p) => {
                const isSelected = selectedProvider === p.id;
                const isAvailable = Boolean(p.url);

                return (
                  <Button
                    key={p.id}
                    onClick={() => isAvailable && setSelectedProvider(p.id)}
                    disabled={!isAvailable}
                    sx={{
                      width: '100%',
                      maxWidth: 560,
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      border: '1px solid #3a4c17',
                      borderRadius: 3,
                      textTransform: 'none',
                      backgroundColor: isSelected ? '#3a4c17' : '#fff',
                      color: isSelected
                        ? '#fff !important'
                        : '#3a4c17 !important',
                      opacity: isAvailable ? 1 : 0.55,
                      boxShadow: isSelected
                        ? '0px 4px 12px rgba(0,0,0,0.15)'
                        : '0px 2px 8px rgba(0,0,0,0.06)',
                      '&:hover': {
                        backgroundColor: isAvailable ? '#3a4c17' : '#fff',
                        color: isAvailable
                          ? '#fff !important'
                          : '#3a4c17 !important',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={p.img}
                      alt={p.label}
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '2px solid #ddd',
                        flexShrink: 0,
                      }}
                    />

                    <Box sx={{ textAlign: 'left' }}>
                      <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                        {p.label}
                      </Typography>

                      {!isAvailable && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: isSelected ? 'white' : 'text.secondary',
                          }}
                        >
                          Scheduling link coming soon
                        </Typography>
                      )}
                    </Box>
                  </Button>
                );
              })}
            </Stack>
          </Box>

          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: 'center',
              fontWeight: 600,
              color: '#3a4c17',
            }}
          >
            Currently viewing schedule for: {activeProvider?.label}
          </Typography>

          {/* Scheduler */}
          <Box sx={{ mt: 3 }}>
            {activeProvider?.url ? (
              <iframe
                title={`${activeProvider.label} Scheduler`}
                src={activeProvider.url}
                width="100%"
                height="600"
                style={{ border: 'none', borderRadius: '12px' }}
              />
            ) : (
              <Typography color="text.secondary" textAlign="center">
                Scheduling link coming soon for{' '}
                <strong>{activeProvider?.label}</strong>.
              </Typography>
            )}
          </Box>

          {/* Bottom CTA */}
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Need help scheduling?
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                href="tel:2108669850"
                sx={{
                  backgroundColor: '#e3f3c5',
                  color: 'black',
                  fontWeight: 600,
                  px: 4,
                  py: 1.3,
                  borderRadius: '30px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#d3eab0',
                  },
                }}
              >
                Call 210-866-9850
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default ScheduleAndProviders;